 // Updated App.js with email verification handling

import React, { useState, useEffect } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline,
  Box,
  CircularProgress
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase-config';
import { authService } from './services/auth-service';

// Components
import LoginForm from './components/LoginForm';
import PasswordResetForm from './components/PasswordResetForm';
import PasswordConfirmForm from './components/PasswordConfirmForm';
import EmailVerification from './components/EmailVerification';
import DashboardLayout from './components/DashboardLayout';
import DashboardHome from './components/DashboardHome';
import CommissionsTable from './components/CommissionsTable';
import CommissionDetail from './components/CommissionDetail';
// import OrderBidding from './components/OrderBidding/OrderBidding'; // Hidden - bids managed by Handoff admin only
import NetworkPage from './components/NetworkPage';
import PartnerMatchesPage from './components/PartnerMatchesPage';
import InvoicesPage from './components/InvoicesPage';
import InvoiceDetail from './components/InvoiceDetail';
import SettingsPage from './components/settings/SettingsPage';
import ConnectPage from './components/connect/ConnectPage';
import RouteTracker from './components/RouteTracker';

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

function App() {
  const [user, setUser] = useState(null);
  const [retailer, setRetailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Auth state changed:', firebaseUser?.email || 'No user');
      
      if (firebaseUser) {
        setUser(firebaseUser);
        
        // Only try API authentication if email is verified OR we're in development
        const isDevelopment = process.env.REACT_APP_ENV === 'development';
        const shouldAuthenticateWithAPI = firebaseUser.emailVerified || isDevelopment;
        
        if (shouldAuthenticateWithAPI) {
          try {
            console.log('🔑 Authenticating with API...');
            const retailerData = await authService.authenticateWithAPI();
            setRetailer(retailerData);
            setAuthError('');
          } catch (error) {
            console.error('❌ API Authentication error:', error);
            setAuthError(error.message);
            setRetailer(null);
          }
        } else {
          console.log('⏳ Waiting for email verification before API authentication');
          setRetailer(null);
        }
      } else {
        setUser(null);
        setRetailer(null);
        setAuthError('');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleVerificationComplete = async (verifiedUser) => {
    console.log('🎉 Email verification completed!');
    
    try {
      setLoading(true);
      const retailerData = await authService.authenticateWithAPI();
      setRetailer(retailerData);
      setAuthError('');
      
      // Update the user state with verified user
      setUser(verifiedUser);
    } catch (error) {
      console.error('❌ API Authentication error after verification:', error);
      setAuthError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          flexDirection="column"
        >
          <CircularProgress />
          <Box mt={2}>Loading...</Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Show login/password reset form if no user
  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/reset-password" element={<PasswordResetForm />} />
            <Route path="/auth/action" element={<PasswordConfirmForm />} />
            <Route path="*" element={<LoginForm />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  // Show email verification if user exists but email not verified
  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  if (!user.emailVerified && !isDevelopment) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <EmailVerification 
          user={user} 
          onVerificationComplete={handleVerificationComplete}
        />
      </ThemeProvider>
    );
  }

  // Show dashboard if user is authenticated and (verified OR in development)
  if (user && (retailer || authError)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <RouteTracker />
          {retailer ? (
            <Routes>
              {/* ChatGPT / Claude sign-in: full screen, outside the dashboard layout */}
              <Route path="/connect" element={<ConnectPage retailer={retailer} />} />
              <Route
                path="*"
                element={
                  <DashboardLayout retailer={retailer}>
                    <Routes>
                      <Route path="/" element={<DashboardHome />} />
                      <Route path="/commissions" element={<CommissionsTable />} />
                      <Route path="/commissions/:id" element={<CommissionDetail />} />
                      <Route path="/invoices" element={<InvoicesPage />} />
                      <Route path="/invoices/:id" element={<InvoiceDetail />} />
                      <Route path="/self-bills/:id" element={<InvoiceDetail kind="self_bill" />} />
                      <Route path="/network" element={<NetworkPage />} />
                      <Route path="/network/:partnerId" element={<PartnerMatchesPage />} />
                      <Route path="/settings/*" element={<SettingsPage />} />
                      {/* Order Bidding hidden - bids now managed by Handoff admin only (8+2 revenue model) */}
                      <Route path="/order-bidding" element={<Navigate to="/" replace />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </DashboardLayout>
                }
              />
            </Routes>
          ) : (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              minHeight="100vh"
              flexDirection="column"
              p={3}
            >
              <Box mb={2}>⚠️ Authentication Error</Box>
              <Box textAlign="center" color="error.main">
                {authError}
              </Box>
              <Box mt={2}>
                <button onClick={() => authService.logout()}>
                  Try Again
                </button>
              </Box>
            </Box>
          )}
        </Router>
      </ThemeProvider>
    );
  }

  // Fallback loading state
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    </ThemeProvider>
  );
}

export default App;